/* eslint-disable no-console */
/**
 * Clasificación POR PROVEEDOR con propagación (Fase A — solo SII recibidas).
 *
 * Clasificar deja de ser una decisión por documento: el proveedor tiene una REGLA que se
 * propaga a sus facturas SII recibidas 2026 sin clasificar. La regla vive en `proveedores`
 * (`clasificacion_modo` + `cuenta_er_default` + `tipo_contable_default`).
 *
 * REGLA DURA: la propagación toca SOLO facturas sin clasificar (tipo_contable y cuenta_er
 * vacíos) — nunca pisa una clasificación manual previa. Las candidatas se resuelven por SELECT
 * y se actualizan por id explícito, así el conjunto es auditable y no depende de un WHERE
 * compuesto.
 *
 * El ER las absorbe solo en la próxima carga: `costosER` lee `cuenta_er`/`tipo_contable` fila
 * por fila (no por proveedor), con el requisito `tipo_contable !== 'activo'` y
 * `fecha_emision >= 2026-01-01`. No se toca el motor.
 *
 * ACTIVO — criterio canónico: `cuenta_er = null` (no `''`). Unifica la asimetría histórica
 * (recibidas escribía '', honorarios null). Ver migración 20260720.
 */
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { FECHA_CORTE } from "../estadoCuenta/saldoInicial";
import { actualizarProveedor } from "../proveedores";
import { client } from "../supabase";

export const MODO = { GASTO: "gasto", ACTIVO: "activo", HIBRIDO: "hibrido" };

const TIPO_DTE_NOTA_CREDITO = 61;
const CATEGORIA_POR_GRUPO = { "Costos Fijos": "costo_fijo", "Costos Variables": "costo_variable" };

/** Categoría del ER derivada de la cuenta (mismo criterio que AsignarRecibidaModal). */
export const categoriaDeCuenta = (cuenta) => {
  const c = CUENTAS_ER.find((x) => x.value === cuenta);
  if (!c) return null;
  return CATEGORIA_POR_GRUPO[c.grupo] || "otro";
};

/**
 * Cambios de clasificación de una factura según el modo. PURO (testeable). Activo → cuenta_er
 * null (canónico). Gasto → la cuenta elegida + su categoría.
 */
export const construirCambiosFactura = (modo, cuentaEr) => {
  if (modo === MODO.ACTIVO) {
    return { tipo_contable: "activo", cuenta_er: null, cuenta_er_categoria: null };
  }
  // Gasto
  return { tipo_contable: "gasto", cuenta_er: cuentaEr, cuenta_er_categoria: categoriaDeCuenta(cuentaEr) };
};

/** Regla que se persiste en el proveedor. PURO. */
export const construirReglaProveedor = (modo, cuentaEr) => {
  if (modo === MODO.HIBRIDO) {
    // Híbrido no fija regla propagable: solo marca el modo para que futuras facturas no se
    // auto-asignen. No se tocan los defaults (podrían venir de un "recordar" viejo, pero ya
    // no gobiernan: el modo manda).
    return { clasificacion_modo: MODO.HIBRIDO };
  }
  if (modo === MODO.ACTIVO) {
    return { clasificacion_modo: MODO.ACTIVO, tipo_contable_default: "activo", cuenta_er_default: null };
  }
  return { clasificacion_modo: MODO.GASTO, tipo_contable_default: "gasto", cuenta_er_default: cuentaEr };
};

/**
 * Predicado de "sin clasificar" / candidata a auto-clasificación. PURO. Mirror del
 * `esCandidataAutoClasificacion` del módulo neutro (supabase/functions/_shared) que usa el
 * ingest — la paridad la fija un test. Excluye NC, respeta el corte de fecha, no pisa una
 * clasificación previa NI un override manual (clasificacion_manual = true).
 */
export const esSinClasificar = (f) =>
  Number(f.tipo_dte) !== TIPO_DTE_NOTA_CREDITO &&
  (f.fecha_emision == null || f.fecha_emision >= FECHA_CORTE) &&
  !f.tipo_contable &&
  !f.cuenta_er &&
  !f.clasificacion_manual;

/**
 * Payload de escritura de la clasificación de una factura, con la MARCA DE ORIGEN. PURO.
 *   manual: false → la puso una regla / automatismo (propagación, ingest).
 *   manual: true  → excepción deliberada de una persona sobre ESA factura (override).
 * La marca es el único predicado que los automatismos consultan para saltear (esSinClasificar).
 */
export const construirWriteClasificacion = (modo, cuentaEr, { manual }) => ({
  ...construirCambiosFactura(modo, cuentaEr),
  clasificacion_manual: manual,
});

/**
 * ¿Puede un automatismo / efecto colateral (p.ej. vincular a caja chica) sobrescribir la
 * clasificación de esta fila? PURO. No, si es un override manual. El vínculo financiero
 * (eje de pago) igual se aplica; solo se saltea el eje de clasificación.
 */
export const puedeAutoescribirClasificacion = (f) => !f.clasificacion_manual;

/**
 * Cambios de clasificación al QUITAR un override de una factura: la vuelve a la regla del
 * proveedor. PURO. Siempre clasificacion_manual = false (deja de ser excepción).
 *   · regla Gasto con cuenta  → re-aplica esa cuenta (gasto).
 *   · regla Activo            → activo.
 *   · sin regla propagable (híbrido / gasto sin cuenta / NULL / sin proveedor) → sin clasificar.
 */
export const construirRevertClasificacion = (regla) => {
  const modo = regla?.clasificacion_modo || null;
  if (modo === MODO.GASTO && regla?.cuenta_er_default) {
    return { ...construirCambiosFactura(MODO.GASTO, regla.cuenta_er_default), clasificacion_manual: false };
  }
  if (modo === MODO.ACTIVO) {
    return { ...construirCambiosFactura(MODO.ACTIVO, null), clasificacion_manual: false };
  }
  return { tipo_contable: null, cuenta_er: null, cuenta_er_categoria: null, clasificacion_manual: false };
};

/**
 * Aplica la regla al proveedor y, si es propagable (gasto/activo), la propaga a sus recibidas
 * 2026 sin clasificar. Devuelve `{ propagadas }`.
 *
 * NO clasifica la factura abierta como caso especial: si está sin clasificar, entra en la
 * propagación como cualquier otra; si ya estaba clasificada, se respeta (regla dura).
 */
export const aplicarReglaProveedor = async ({ proveedorId, pais, modo, cuentaEr }) => {
  if (!proveedorId) throw new Error("aplicarReglaProveedor requiere proveedorId");

  await actualizarProveedor(proveedorId, construirReglaProveedor(modo, cuentaEr));

  // Híbrido no propaga.
  if (modo === MODO.HIBRIDO) return { propagadas: 0 };

  // Candidatas: recibidas del proveedor, del país, sin clasificar. Se traen y se filtran con
  // el MISMO predicado puro (esSinClasificar) que testea el ER — una sola definición.
  const { data, error } = await client
    .from("facturas_sii_recibidas")
    .select("id, tipo_dte, fecha_emision, tipo_contable, cuenta_er, clasificacion_manual")
    .eq("pais", pais)
    .eq("proveedor_id", proveedorId)
    .gte("fecha_emision", FECHA_CORTE);
  if (error) throw error;

  // `esSinClasificar` ya excluye los overrides manuales (clasificacion_manual = true): la
  // propagación por regla NUNCA pisa una excepción puesta a mano.
  const ids = (data || []).filter(esSinClasificar).map((f) => f.id);
  if (ids.length === 0) return { propagadas: 0 };

  // Escrito por REGLA → clasificacion_manual = false.
  const cambios = construirWriteClasificacion(modo, cuentaEr, { manual: false });
  const { error: updErr } = await client
    .from("facturas_sii_recibidas")
    .update({ ...cambios, vinculado_at: new Date().toISOString() })
    .in("id", ids);
  if (updErr) throw updErr;

  return { propagadas: ids.length };
};

/**
 * Payload del carve-out (reparto manual a una 2ª cuenta del ER) para el write per-factura. PURO.
 * Solo tiene sentido en GASTO. Se ESCRIBE SIEMPRE (aunque sea a null) para que reclasificar una
 * factura borre un reparto anterior en vez de dejarlo colgado. Válido = cuenta secundaria +
 * monto > 0; cualquier otra cosa (activo, sin carve-out, monto vacío) limpia a null.
 */
export const construirCarveOut = (modo, carveOut) => {
  if (modo === MODO.GASTO && carveOut && carveOut.cuentaEr2 && Number(carveOut.montoEr2) > 0) {
    return { cuenta_er_2: carveOut.cuentaEr2, monto_er_2: Number(carveOut.montoEr2) };
  }
  return { cuenta_er_2: null, monto_er_2: null };
};

/**
 * Clasifica UNA sola factura (path híbrido: la factura abierta se clasifica pero no se
 * propaga). Mismo write que la propagación, sobre un id. `carveOut` opcional reparte parte del
 * neto a una 2ª cuenta (ver construirCarveOut); si no viene, se limpia a null.
 */
export const clasificarFacturaUnica = async ({ facturaId, modo, cuentaEr, carveOut = null }) => {
  // Decisión DELIBERADA sobre una factura puntual → clasificacion_manual = true (override).
  const cambios = construirWriteClasificacion(modo, cuentaEr, { manual: true });
  const carve = construirCarveOut(modo, carveOut);
  const { error } = await client
    .from("facturas_sii_recibidas")
    .update({ ...cambios, ...carve, vinculado_at: new Date().toISOString() })
    .eq("id", facturaId);
  if (error) throw error;
};

/**
 * Quita el override de UNA factura y la devuelve a la regla del proveedor (O-2). Lee la regla
 * viva del proveedor y aplica `construirRevertClasificacion`. Toca SOLO el eje de clasificación
 * (+ la marca y `vinculado_at`); nada del eje de pago.
 */
export const volverAReglaDelProveedor = async ({ facturaId, proveedorId }) => {
  let regla = null;
  if (proveedorId) {
    const { data, error } = await client
      .from("proveedores")
      .select("clasificacion_modo, cuenta_er_default")
      .eq("id", proveedorId)
      .single();
    if (error) throw error;
    regla = data;
  }
  const cambios = construirRevertClasificacion(regla);
  // Volver a la regla borra cualquier carve-out manual de la factura: la regla del proveedor no
  // reparte, así que la 2ª cuenta no tiene sentido fuera del override.
  const { error: updErr } = await client
    .from("facturas_sii_recibidas")
    .update({ ...cambios, cuenta_er_2: null, monto_er_2: null, vinculado_at: new Date().toISOString() })
    .eq("id", facturaId);
  if (updErr) throw updErr;
};

// ─── Desvincular regla (O-3): limpia la regla Y revierte lo que la regla propagó ──────────

/** La regla del proveedor a NULL. Constante PURA (testeable). */
export const REGLA_PROVEEDOR_LIMPIA = {
  clasificacion_modo: null,
  cuenta_er_default: null,
  tipo_contable_default: null,
};

/**
 * Payload de reversión de UNA factura a sin clasificar. Constante PURA. SOLO el eje de
 * clasificación — ni `estado_vinculacion`, ni `vinculado_por/at`, ni MBF, ni plan de pagos.
 */
export const REVERT_A_SIN_CLASIFICAR = { cuenta_er: null, tipo_contable: null, cuenta_er_categoria: null };

/** Dentro de la ventana del ER y no es NC (las NC nunca se clasifican). PURO. */
const enVentanaClasificable = (f) =>
  Number(f.tipo_dte) !== TIPO_DTE_NOTA_CREDITO && (f.fecha_emision == null || f.fecha_emision >= FECHA_CORTE);

/**
 * ¿Esta factura la revierte el desvínculo de la regla? PURO. Sí, si está clasificada (tiene
 * cuenta o tipo) y NO es un override manual — o sea, la puso la regla/automatismo. Las
 * clasificacion_manual = true (excepciones) sobreviven; las NC no aplican.
 */
export const esRevertiblePorDesvinculo = (f) =>
  enVentanaClasificable(f) && !f.clasificacion_manual && Boolean(f.tipo_contable || f.cuenta_er);

/**
 * Particiona las recibidas 2026 de un proveedor para el desvínculo. PURO (recibe las filas ya
 * traídas). Devuelve los ids a revertir y los conteos del diálogo de confirmación:
 *   · revertidas = clasificadas por la regla (manual = false) → vuelven a sin clasificar.
 *   · manuales   = excepciones manuales (manual = true) → se conservan intactas.
 */
export const resumenDesvinculo = (facturas) => {
  const lista = facturas || [];
  const revertibles = lista.filter(esRevertiblePorDesvinculo);
  const manuales = lista.filter((f) => enVentanaClasificable(f) && Boolean(f.clasificacion_manual));
  return { idsARevertir: revertibles.map((f) => f.id), revertidas: revertibles.length, manuales: manuales.length };
};

/** Trae las SII recibidas 2026 del proveedor con los campos que deciden el desvínculo. */
const traerRecibidasDelProveedor = async (proveedorId, pais) => {
  const { data, error } = await client
    .from("facturas_sii_recibidas")
    .select("id, tipo_dte, fecha_emision, tipo_contable, cuenta_er, clasificacion_manual")
    .eq("pais", pais)
    .eq("proveedor_id", proveedorId)
    .gte("fecha_emision", FECHA_CORTE);
  if (error) throw error;
  return data || [];
};

/** Preview del desvínculo: conteos reales (N a revertir, M manuales) para el diálogo. Read-only. */
export const previewDesvinculoRegla = async ({ proveedorId, pais }) => {
  const facturas = await traerRecibidasDelProveedor(proveedorId, pais);
  const { revertidas, manuales } = resumenDesvinculo(facturas);
  return { revertidas, manuales };
};

/**
 * Desvincula la regla del proveedor Y revierte a sin clasificar las facturas que la regla /
 * automatismo propagó (manual = false), preservando las excepciones manuales (manual = true).
 * Scope: ese proveedor, país, 2026. Solo el eje de clasificación; nada del de pago. Devuelve
 * los conteos efectivamente aplicados.
 */
export const desvincularReglaProveedor = async ({ proveedorId, pais }) => {
  const facturas = await traerRecibidasDelProveedor(proveedorId, pais);
  const { idsARevertir, revertidas, manuales } = resumenDesvinculo(facturas);

  // 1. Limpiar la regla del proveedor (como antes).
  await actualizarProveedor(proveedorId, REGLA_PROVEEDOR_LIMPIA);

  // 2. Revertir a sin clasificar SOLO las propagadas por la regla. Las manual = true quedan
  //    intactas (no entran en idsARevertir). Nada del eje de pago.
  if (idsARevertir.length > 0) {
    const { error } = await client
      .from("facturas_sii_recibidas")
      .update(REVERT_A_SIN_CLASIFICAR)
      .in("id", idsARevertir);
    if (error) throw error;
  }

  return { revertidas, manuales };
};
